import {
  addScheduleRuleProperties,
  ScheduleRule,
  getScheduleRule as getQuery,
  createScheduleRule as createMutation,
  updateScheduleRule as updateMutation,
  deleteScheduleRule as deleteMutation,
  CreateScheduleRuleInput,
  cleanInputUpdate,
  cleanInputCreate,
  getReadId,
  getTableClientId,
  Model,
  OnCreateScheduleRuleSubscription,
  OnUpdateScheduleRuleSubscription,
  OnDeleteScheduleRuleSubscription,
  CreateScheduleRuleMutationVariables,
  DeleteScheduleRuleMutationVariables,
  UpdateScheduleRuleInput,
  syncScheduleRules,
  onCreateScheduleRule,
  onUpdateScheduleRule,
  onDeleteScheduleRule,
} from '@rentguru/commons-utils';
import {
  cleanMixedDatastoreAndAPISubscription,
  deleteEntityWithFetchBefore,
  getFilterFieldNameForIndex,
  list,
  mutation,
  useSubscriptions,
} from '@up2rent/graphql';
import isNil from 'lodash/isNil';
import { createContext, Reducer, useContext, useEffect, useMemo, useReducer, useRef } from 'react';

import { useUser } from './UserContext';

const ENTITY_MODEL_NAME: Model = 'ScheduleRule';

const customGetScheduleRule = addScheduleRuleProperties(getQuery);
const customCreateScheduleRule = addScheduleRuleProperties(createMutation);
const customUpdateScheduleRule = addScheduleRuleProperties(updateMutation);
const customSyncScheduleRules = addScheduleRuleProperties(syncScheduleRules);
const customOnCreateScheduleRule = addScheduleRuleProperties(
  cleanMixedDatastoreAndAPISubscription(onCreateScheduleRule)
);
const customOnUpdateScheduleRule = addScheduleRuleProperties(
  cleanMixedDatastoreAndAPISubscription(onUpdateScheduleRule)
);
const customOnDeleteScheduleRule = addScheduleRuleProperties(
  cleanMixedDatastoreAndAPISubscription(onDeleteScheduleRule)
);

export interface RuleContextState extends RuleState {
  createRule: (input: Omit<CreateScheduleRuleInput, 'id' | 'clientId' | 'readId'>) => Promise<ScheduleRule>;
  getRulesByForeignKey: (foreignKey: string, foreignTableName: string) => ScheduleRule[];
  getRule: (id: string) => ScheduleRule | undefined;
  updateRule: (input: UpdateScheduleRuleInput) => Promise<ScheduleRule>;
  deleteRule: (id: string) => Promise<ScheduleRule | undefined>;
  setFetchRules: () => void;
}

interface RuleState {
  rules: ScheduleRule[] | null;
  loading: boolean;
  shouldFetch: boolean;
}

type Action =
  | { type: 'SHOULD_FETCH' | 'IS_FETCHING' }
  | { type: 'ADD_RULE'; payload: ScheduleRule }
  | { type: 'FETCHED'; payload: { rules: ScheduleRule[] } }
  | { type: 'FETCH_FAILED' }
  | { type: 'UPDATE_RULE'; payload: ScheduleRule }
  | { type: 'DELETE_RULE'; payload: { id: string } };

const initialState: RuleState = {
  loading: false,
  rules: null,
  shouldFetch: false,
};

const ruleReducer = (state: RuleState, action: Action): RuleState => {
  switch (action.type) {
    case 'SHOULD_FETCH':
      if (state.loading || state.shouldFetch) return state;
      return { ...state, shouldFetch: true };
    case 'IS_FETCHING':
      return { ...state, loading: true };
    case 'FETCHED':
      return {
        ...state,
        loading: false,
        shouldFetch: false,
        rules: action.payload.rules,
      };
    case 'FETCH_FAILED':
      return { ...state, loading: false, shouldFetch: false };
    case 'ADD_RULE':
      if (state.rules?.find((rule) => rule.id === action.payload.id)) return state;
      return {
        ...state,
        rules: [...(state.rules ?? []), action.payload],
      };
    case 'UPDATE_RULE':
      if (!state.rules) return state;
      return {
        ...state,
        rules: state.rules.map((rule) => (rule.id === action.payload.id ? action.payload : rule)),
      };
    case 'DELETE_RULE':
      if (!state.rules) return state;
      return {
        ...state,
        rules: state.rules.filter((rule) => rule.id !== action.payload.id),
      };
    default:
      return state;
  }
};

export const RuleContext = createContext<RuleContextState | null>(null);

export const RuleContextProvider = ({ children }: { children?: React.ReactNode }) => {
  const [state, dispatch] = useReducer<Reducer<RuleState, Action>>(ruleReducer, initialState);
  const { clientId } = useUser();

  const setFetchRules = () => dispatch({ type: 'SHOULD_FETCH' });

  useEffect(() => {
    const fetchAndSet = async () => {
      if (!clientId) {
        return;
      }
      dispatch({ type: 'IS_FETCHING' });

      try {
        const result = await list<ScheduleRule>(
          customSyncScheduleRules,
          getFilterFieldNameForIndex('byClientId'),
          getTableClientId(clientId, ENTITY_MODEL_NAME)
        );
        dispatch({ type: 'FETCHED', payload: { rules: result } });
      } catch (error) {
        console.error('[rules] fetching schedule rules failed', error);
        dispatch({ type: 'FETCH_FAILED' });
      }
    };

    if (state.shouldFetch) {
      fetchAndSet();
    }
  }, [state.shouldFetch, clientId]);

  useSubscriptions<
    OnCreateScheduleRuleSubscription,
    OnUpdateScheduleRuleSubscription,
    OnDeleteScheduleRuleSubscription
  >(
    customOnCreateScheduleRule,
    customOnUpdateScheduleRule,
    customOnDeleteScheduleRule,
    (data) => dispatch({ type: 'ADD_RULE', payload: data.onCreateScheduleRule as ScheduleRule }),
    (data) => dispatch({ type: 'UPDATE_RULE', payload: data.onUpdateScheduleRule as ScheduleRule }),
    (data) => {
      const { id } = data.onDeleteScheduleRule as ScheduleRule;
      dispatch({ type: 'DELETE_RULE', payload: { id } });
    }
  );

  const createRule = async (
    input: Omit<CreateScheduleRuleInput, 'id' | 'clientId' | 'readId'>
  ): Promise<ScheduleRule> => {
    if (!clientId) {
      throw new Error('Cannot create Rule without a valid clientId');
    }
    const rule = await mutation<ScheduleRule, CreateScheduleRuleMutationVariables>(customCreateScheduleRule, {
      input: {
        ...(cleanInputCreate(input) as CreateScheduleRuleInput),
        clientId: getTableClientId(clientId, ENTITY_MODEL_NAME),
        readId: getReadId(clientId, ENTITY_MODEL_NAME),
      },
    });
    dispatch({ type: 'ADD_RULE', payload: rule });
    return rule;
  };

  const updateRule = async (input: UpdateScheduleRuleInput): Promise<ScheduleRule> => {
    const result = await mutation<ScheduleRule>(customUpdateScheduleRule, {
      input: cleanInputUpdate(input, false),
    });
    dispatch({ type: 'UPDATE_RULE', payload: result });
    return result;
  };

  const deleteRule = async (id: string): Promise<ScheduleRule | undefined> => {
    const rule = getRule(id);
    if (!rule) {
      return;
    }
    await deleteEntityWithFetchBefore<Pick<ScheduleRule, 'id'>, DeleteScheduleRuleMutationVariables>(
      { id },
      customGetScheduleRule,
      deleteMutation
    );
    dispatch({ type: 'DELETE_RULE', payload: { id } });
    return rule;
  };

  const getRule = (id: string) => (state.rules ?? []).find((rule) => rule.id === id);
  const getRulesByForeignKey = (foreignKey: string, foreignTableName: string) => {
    if (isNil(state.rules)) return [];
    return state.rules
      .filter((rule) => rule.foreignKey === foreignKey && rule.foreignTableName === foreignTableName)
      .sort((first, second) => (first.order ?? 0) - (second.order ?? 0));
  };

  const values = useMemo(
    () => ({
      ...state,
      createRule,
      updateRule,
      deleteRule,
      getRule,
      getRulesByForeignKey,
      setFetchRules,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state]
  );

  return <RuleContext.Provider value={values}>{children}</RuleContext.Provider>;
};

export const useRules = (): RuleContextState => {
  const firstRender = useRef<boolean>(true);
  const context = useContext<RuleContextState | null>(RuleContext);

  if (isNil(context)) {
    throw new Error('`useRules` hook must be used within a `RuleContextProvider` component');
  }

  useEffect(() => {
    if (firstRender.current && isNil(context.rules)) {
      context.setFetchRules();
      firstRender.current = false;
    }
  }, [context]);

  return context;
};
